// Pure scope resolver. Given a `definitions` moddle root and a Decision or
// BKM element, returns the list of variables visible from inside that
// element's logic — i.e. what a FEEL expression can legally reference.
//
// Output shape (flat array):
//   {
//     kind:       'input' | 'decision' | 'function' | 'param' | 'self',
//     name:       string,                 // identifier used in FEEL
//     typeRef:    string | null,          // e.g. 'integer', 'double', 'string'
//     sourceId:   string | null,          // moddle id of the originating element
//     sourceName: string | null,          // human-readable source label
//     sourceKind: string,                 // 'InputData' | 'Decision' | 'BusinessKnowledgeModel' | 'Parameter'
//     signature?: { name, typeRef }[],    // only for kind==='function'
//   }
//
// MVP scope — does NOT yet descend into Context entries to expose locally
// declared variables (that lives in a richer "scope at cursor" API planned
// for Phase 2, where the caller will pass a path into the expression tree).

import { sanitizeFeelIdent } from '../feel/ident';

// `displayName` here returns the FEEL identifier used to reference `el`
// from inside a FEEL expression — not a human-readable label. When falling
// back to `el.name`, strip whitespace so the value matches what the FEEL
// parser would tokenize (FEEL idents have no embedded spaces in this editor).
function displayName(el) {
  if (!el) return '';
  if (el.variable && el.variable.name) return el.variable.name;
  return sanitizeFeelIdent(el.name) || el.id || '';
}

function typeRefOf(el) {
  if (!el) return null;
  if (el.variable && el.variable.typeRef) return el.variable.typeRef;
  return null;
}

function stripHref(href) {
  return (href || '').split('#').pop();
}

export function displayType(typeRef) {
  if (!typeRef) return 'any';
  return String(typeRef).replace(/^[^:]+:/, '');
}

export function computeScope(definitions, element) {
  if (!definitions || !element) return [];
  const byId = new Map();
  (definitions.drgElement || []).forEach((e) => byId.set(e.id, e));

  const out = [];

  if (element.$type === 'dmn:Decision') {
    (element.informationRequirement || []).forEach((req) => {
      const inputRef = req.requiredInput && stripHref(req.requiredInput.href);
      const decRef = req.requiredDecision && stripHref(req.requiredDecision.href);
      const id = inputRef || decRef;
      if (!id) return;
      const target = byId.get(id);
      if (!target) return;
      out.push({
        kind: inputRef ? 'input' : 'decision',
        name: displayName(target),
        typeRef: typeRefOf(target),
        sourceId: target.id,
        sourceName: target.name || target.id,
        sourceKind: target.$type.replace(/^dmn:/, ''),
      });
    });

    (element.knowledgeRequirement || []).forEach((kr) => {
      const id = kr.requiredKnowledge && stripHref(kr.requiredKnowledge.href);
      if (!id) return;
      const target = byId.get(id);
      if (!target) return;
      const params = (target.encapsulatedLogic && target.encapsulatedLogic.formalParameter) || [];
      out.push({
        kind: 'function',
        name: sanitizeFeelIdent(target.name) || target.id,
        typeRef: typeRefOf(target),
        sourceId: target.id,
        sourceName: target.name || target.id,
        sourceKind: target.$type.replace(/^dmn:/, ''),
        signature: params.map((p) => ({
          name: p.name || '',
          typeRef: p.typeRef || null,
        })),
      });
    });

    out.push({
      kind: 'self',
      name: displayName(element),
      typeRef: typeRefOf(element),
      sourceId: element.id,
      sourceName: element.name || element.id,
      sourceKind: 'Decision',
    });
  } else if (element.$type === 'dmn:BusinessKnowledgeModel') {
    const params = (element.encapsulatedLogic && element.encapsulatedLogic.formalParameter) || [];
    params.forEach((p) => {
      out.push({
        kind: 'param',
        name: p.name || '',
        typeRef: p.typeRef || null,
        sourceId: p.id || null,
        sourceName: element.name || element.id,
        sourceKind: 'Parameter',
      });
    });
    out.push({
      kind: 'self',
      name: displayName(element),
      typeRef: typeRefOf(element),
      sourceId: element.id,
      sourceName: element.name || element.id,
      sourceKind: 'BusinessKnowledgeModel',
    });
  }

  return out;
}
